import type { IAuthRepository, User, Session } from '@inventory/core';
import { UserPouchDBAdapter } from './UserPouchDBAdapter';
import { SessionPouchDBAdapter } from './SessionPouchDBAdapter';

export class AuthPouchDBAdapter implements IAuthRepository {
  private readonly userAdapter: UserPouchDBAdapter;
  private readonly sessionAdapter: SessionPouchDBAdapter;

  constructor(dbName: string) {
    this.userAdapter = new UserPouchDBAdapter(dbName);
    this.sessionAdapter = new SessionPouchDBAdapter(dbName);
    this.createIndices();
  }

  private createIndices(): void {
    this.userAdapter.createIndex(['username']).catch(() => {});
    this.userAdapter.createIndex(['email']).catch(() => {});
    this.userAdapter.createIndex(['role']).catch(() => {});
    this.userAdapter.createIndex(['active']).catch(() => {});
    this.sessionAdapter.createIndex(['userId']).catch(() => {});
    this.sessionAdapter.createIndex(['token']).catch(() => {});
    this.sessionAdapter.createIndex(['refreshToken']).catch(() => {});
    this.sessionAdapter.createIndex(['active']).catch(() => {});
  }

  // User Operations
  async createUser(user: User): Promise<User> {
    const existing =
      (await this.getUserByUsername(user.username)) ?? (await this.getUserByEmail(user.email));
    if (existing) {
      throw new Error('Username or email already exists');
    }
    await this.userAdapter.save(user);
    return user;
  }

  async updateUser(user: User): Promise<User> {
    await this.userAdapter.save(user);
    return user;
  }

  async deleteUser(id: string): Promise<void> {
    await this.userAdapter.delete(id);
  }

  async getUserById(id: string): Promise<User | null> {
    return this.userAdapter.findById(id);
  }

  async getUserByUsername(username: string): Promise<User | null> {
    return this.userAdapter.findByUsername(username);
  }

  async getUserByEmail(email: string): Promise<User | null> {
    return this.userAdapter.findByEmail(email);
  }

  // Session Operations
  async createSession(session: Session): Promise<Session> {
    await this.sessionAdapter.save(session);
    return session;
  }

  async updateSession(session: Session): Promise<Session> {
    await this.sessionAdapter.save(session);
    return session;
  }

  async deleteSession(id: string): Promise<void> {
    await this.sessionAdapter.delete(id);
  }

  async getSessionById(id: string): Promise<Session | null> {
    return this.sessionAdapter.findById(id);
  }

  async getSessionsByUserId(userId: string): Promise<Session[]> {
    return this.sessionAdapter.findByUserId(userId);
  }

  async deleteSessionsByUserId(userId: string): Promise<void> {
    const sessions = await this.getSessionsByUserId(userId);
    await Promise.all(sessions.map(s => this.deleteSession(s.id)));
  }
}
